import { createContext, useContext, useMemo } from 'react';

import type {
  UseAccountOptionsFetch,
  UseCreateProvider,
  UseEditProvider,
  UseEditProviderAllowedAccounts,
  UseGetProvidersTableData,
  UseProviderFetch,
  UseProviderPageHeaderActions,
  UseProvidersListFetch,
} from './ProviderHookTypes';

export type ProviderContextConfig = {
  isOa?: boolean;
  useAccountOptionsFetch: UseAccountOptionsFetch;
  useCreateProvider: UseCreateProvider;
  useEditProvider: UseEditProvider;
  useEditProviderAllowedAccounts: UseEditProviderAllowedAccounts;
  useGetProvidersTableData: UseGetProvidersTableData;
  useProvidersListFetch: UseProvidersListFetch;
  useProviderFetch: UseProviderFetch;
  useProviderPageHeaderActions: UseProviderPageHeaderActions;
};

export type ProviderContextProviderProps = {
  children: React.ReactNode;
  providerConfig: ProviderContextConfig;
};

export type ProviderContext = ProviderContextConfig;

const providerContext = createContext<ProviderContext>({
  isOa: false,
  useAccountOptionsFetch: () => {
    throw new Error('useCreateProvider: Not implemented. Ensure you are inside the ProviderContext');
  },
  useCreateProvider: () => {
    throw new Error('useCreateProvider: Not implemented. Ensure you are inside the ProviderContext');
  },
  useEditProvider: () => {
    throw new Error('useEditProvider: Not implemented. Ensure you are inside the ProviderContext');
  },
  useEditProviderAllowedAccounts: () => {
    throw new Error('useEditProviderAllowedAccounts: Not implemented. Ensure you are inside the ProviderContext');
  },
  useGetProvidersTableData: () => {
    throw new Error('useGetProvidersTableData: Not implemented. Ensure you are inside the ProviderContext');
  },
  useProviderFetch: () => {
    throw new Error('useProviderFetch: Not implemented. Ensure you are inside the ProviderContext');
  },
  useProviderPageHeaderActions: () => {
    throw new Error('useProviderPageHeaderActions: Not implemented. Ensure you are inside the ProviderContext');
  },
  useProvidersListFetch: () => {
    throw new Error('useProvidersListFetch: Not implemented. Ensure you are inside the ProviderContext');
  },
});

export const useProviderContext = () => useContext(providerContext);

export function ProviderContextProvider({ children, providerConfig }: ProviderContextProviderProps) {
  const value: ProviderContext = useMemo(
    () => ({
      ...providerConfig,
    }),
    [providerConfig]
  );

  return <providerContext.Provider value={value}>{children}</providerContext.Provider>;
}
